import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Button, ButtonGroup, CalendarDate, Check, ChevronDown, ClockRewind, Drawer, Eye, LayoutAlt01, Menu, SearchMd,
  Send01, TextInput,
} from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import TemplateEditor from "../../BeefreeTemplates/TemplateEditor";
import TemplateHTMLPreview from "../../BeefreeTemplates/TemplateHTMLPreview";
import VersionHistoryPanel, { formatCount, relativeTime, useTemplateVersions } from "../VersionHistoryPanel";
import EmailPreviewTab from "../EmailPreviewTab";
import { previewDrawerWidth } from "../emailPreviewWidth";
import SendTestOverlay from "../../BeefreeTemplates/SendTestOverlay";
import ErrorsContainer from "../../BeefreeTemplates/ErrorsContainer";
import { templateSelectionParams } from "../broadcastTemplates";
import { toastError, toastSuccess } from "../../../../shared/toastHelper";
import BlueprintEventPicker, { applyBlueprint } from "./BlueprintEventPicker";
import { retargetEvent, templateEvent } from "./templateEvent";
import { BlueprintCard, BlueprintGallery } from "../../shared/BlueprintGallery";

const styles = {
  thumbnailFrame: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 600,
    height: 540,
    border: "none",
    transform: "scale(0.34)",
    transformOrigin: "0 0",
    pointerEvents: "none",
  },
  scratch: {
    height: "100%",
    display: "grid",
    placeItems: "center",
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "22px 24px",
    borderBottom: "1px solid var(--sl-border-secondary)",
    whiteSpace: "nowrap",
  },
  group: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  templateDetails: {
    minWidth: 0,
  },
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tile: {
    width: 40,
    height: 40,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    padding: 0,
    borderRadius: "var(--sl-radius-md)",
    border: "1px solid var(--sl-border-secondary)",
    background: "var(--sl-bg-tertiary)",
    color: "var(--sl-text-quaternary)",
    cursor: "pointer",
  },
  metaButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: 0,
    border: "none",
    background: "transparent",
    font: "inherit",
    color: "inherit",
    cursor: "pointer",
  },
  galleryPage: {
    padding: 32,
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    lineHeight: "18px",
    color: "var(--sl-text-quaternary)",
  },
  divider: {
    width: 1,
    height: 32,
    background: "var(--sl-border-secondary)",
  },
  eventChip: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "4px 8px 4px 4px",
    border: "none",
    borderRadius: "var(--sl-radius-lg)",
    background: "transparent",
    textAlign: "left",
    cursor: "pointer",
  },
  dateTile: {
    width: 40,
    height: 40,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "hidden",
    borderRadius: "var(--sl-radius-md)",
    border: "1px solid var(--sl-border-secondary)",
    background: "var(--sl-bg-primary)",
    color: "var(--sl-text-primary)",
  },
  dateTileMonth: {
    width: "100%",
    textAlign: "center",
    fontSize: 10,
    lineHeight: "14px",
    fontWeight: 700,
    color: "var(--sl-text-white)",
    background: "var(--sl-bg-brand-solid)",
  },
  dateTileDay: {
    fontSize: 16,
    lineHeight: "24px",
    fontWeight: 700,
  },
  menuHeader: {
    padding: "10px 14px 4px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--sl-text-quaternary)",
  },
  menuNote: {
    padding: "8px 2px 0",
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
    whiteSpace: "normal",
  },
};

const reloadIntoEditor = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("step", "content");
  window.location.href = url.toString();
};

const TemplateGallery = ({ heading, replacingDesign, onKeepDesign }) => {
  const {
    team,
    broadcast,
    updateBroadcast,
    adminBlueprints,
    fetchAdminBlueprints,
    teamBlueprints,
    fetchTeamBlueprints,
    previousBroadcastTemplates,
    fetchPreviousBroadcastTemplates,
  } = useBroadcastEditContext();

  const [source, setSource] = useState("all");
  const [pickingFor, setPickingFor] = useState(null);
  const [applyingBlueprint, setApplyingBlueprint] = useState(false);

  useEffect(() => {
    fetchAdminBlueprints();
    fetchTeamBlueprints();
    fetchPreviousBroadcastTemplates();
  }, []);

  const select = (template, kind) => {
    updateBroadcast(templateSelectionParams(broadcast, template, kind), false, reloadIntoEditor);
  };

  const selectAdminBlueprint = (blueprint) => {
    if (blueprint.event_picker) {
      setPickingFor(blueprint);
    } else if (blueprint.catalog_key) {
      if (applyingBlueprint) return;
      setApplyingBlueprint(true);
      applyBlueprint(team.id, broadcast.id, blueprint.id)
        .then(reloadIntoEditor)
        .catch((error) => {
          setApplyingBlueprint(false);
          toastError(error?.response?.data?.errors?.[0] || "Could not apply that template, please try again");
        });
    } else {
      select(blueprint, { isBlueprint: true });
    }
  };

  const groups = [
    {
      value: "opendate",
      label: "Opendate blueprints",
      cards: adminBlueprints.map((blueprint) => ({
        key: `blueprint-${blueprint.id}`,
        name: blueprint.name,
        html: blueprint.template_html,
        onSelect: () => selectAdminBlueprint(blueprint),
      })),
    },
    {
      value: "team",
      label: "Team blueprints",
      cards: teamBlueprints.map((blueprint) => ({
        key: `team-blueprint-${blueprint.id}`,
        name: blueprint.name,
        html: blueprint.template_html,
        onSelect: () => select(blueprint, { isTeamBlueprint: true }),
      })),
    },
    {
      value: "previous",
      label: "Previous broadcasts",
      cards: previousBroadcastTemplates
        .filter((template) => template.templatable.id !== broadcast.id)
        .map((template) => ({
          key: `previous-${template.id}`,
          name: template.templatable.name,
          html: template.template_html,
          onSelect: () => select(template),
        })),
    },
  ];

  const scratchCard = { key: "scratch", name: "Start from scratch", html: null, onSelect: () => select(null) };
  const allCards = groups.flatMap((group) => group.cards);
  const visibleCards = source === "all" ? allCards : groups.find((group) => group.value === source).cards;

  return (
    <>
      {heading}
      {replacingDesign && (
        <div style={{ marginTop: 16 }}>
          <Alert
            color="warning"
            title="Picking a new template replaces your current design."
            actions={<Button variant="link-color" size="sm" onClick={onKeepDesign}>Keep current design</Button>}
          />
        </div>
      )}
      <BlueprintGallery
        caption="What are you sending?"
        ariaLabel="Template sources"
        tabs={[
          { value: "all", label: `All (${allCards.length + 1})` },
          ...groups.map((group) => ({ value: group.value, label: `${group.label} (${group.cards.length})` })),
        ]}
        value={source}
        onChange={setSource}
      >
        {[scratchCard, ...visibleCards].map((card) => (
          <BlueprintCard key={card.key} name={card.name} onSelect={card.onSelect}>
            {card.html ? (
              <TemplateHTMLPreview htmlContent={card.html} style={styles.thumbnailFrame} disableLinks />
            ) : (
              <div style={styles.scratch}>Blank canvas</div>
            )}
          </BlueprintCard>
        ))}
      </BlueprintGallery>
      {pickingFor && (
        <BlueprintEventPicker
          teamId={team.id}
          broadcastId={broadcast.id}
          blueprint={pickingFor}
          onSkip={() => { setPickingFor(null); select(pickingFor, { isBlueprint: true }); }}
          onApplied={reloadIntoEditor}
          onClose={() => setPickingFor(null)}
        />
      )}
    </>
  );
};

const useSavedLabel = (isAutosaving, lastSavedAt) => {
  const [savedAt, setSavedAt] = useState(lastSavedAt ? Date.parse(lastSavedAt) : null);
  const [, setNow] = useState(0);
  const wasSaving = useRef(false);

  useEffect(() => {
    if (wasSaving.current && !isAutosaving) setSavedAt(Date.now());
    wasSaving.current = isAutosaving;
  }, [isAutosaving]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  if (isAutosaving) return "Saving…";
  return savedAt ? `Saved ${relativeTime(savedAt)}` : "Saved";
};

const VersionHistoryMenu = ({ savedLabel, onSeeAll }) => {
  const { versions, hasLoaded, load, restore } = useTemplateVersions();

  const versionItems = versions.slice(0, 5).map((version) => ({
    key: version.id,
    label: relativeTime(version.created_at),
    description: [formatCount(version.char_count), version.whodunnit_name].filter(Boolean).join(" · "),
    shortcut: "Restore",
    onClick: () => restore(version),
  }));

  const items = hasLoaded && versionItems.length === 0
    ? [{ key: "empty", label: "No previous versions yet", disabled: true }]
    : versionItems;

  return (
    <Menu
      minWidth={320}
      onOpenChange={(open) => open && load(1)}
      header={<div style={styles.menuHeader}>Version history</div>}
      items={[...items, { divider: true }, { key: "all", label: "See all versions", icon: ClockRewind, onClick: onSeeAll }]}
      trigger={(
        <button type="button" style={styles.metaButton}>
          <ClockRewind width={12} height={12} aria-hidden="true" />
          {savedLabel}
          <ChevronDown width={12} height={12} aria-hidden="true" />
        </button>
      )}
    />
  );
};

const DateTile = ({ month, day }) => (
  <span style={styles.dateTile} aria-hidden="true">
    <span style={styles.dateTileMonth}>{month}</span>
    <span style={styles.dateTileDay}>{day}</span>
  </span>
);

const EventMenu = ({ eventId, onChange }) => {
  const { team, broadcast } = useBroadcastEditContext();
  const [events, setEvents] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios.get(`/teams/${team.id}/broadcasts/${broadcast.id}/blueprint_events`)
      .then(({ data }) => setEvents(data))
      .catch(() => setEvents([]));
  }, [team.id, broadcast.id]);

  const current = events?.find((event) => event.value === eventId);
  const matches = (events || []).filter((event) => event.title.toLowerCase().includes(query.trim().toLowerCase()));

  const trigger = eventId ? (
    <button type="button" style={styles.eventChip} aria-label="Change event">
      {current && <DateTile month={current.month} day={current.day} />}
      <span>
        <span style={styles.title}>
          {current?.title || (events ? "Event" : "Loading event…")}
          <ChevronDown width={14} height={14} color="var(--sl-text-quaternary)" aria-hidden="true" />
        </span>
        {current && <span style={styles.meta}>{current.when}</span>}
      </span>
    </button>
  ) : (
    <Button variant="secondary" size="sm" icon={CalendarDate}>Choose event</Button>
  );

  return (
    <Menu
      minWidth={360}
      onOpenChange={(open) => open && setQuery("")}
      header={(
        <div style={{ padding: "12px 12px 4px" }} onKeyDown={(event) => event.key !== "Escape" && event.stopPropagation()}>
          <TextInput
            size="sm"
            leadingIcon={SearchMd}
            aria-label="Search events"
            placeholder="Search events"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div style={styles.menuNote}>Event merge fields update to the new event.</div>
        </div>
      )}
      items={matches.length > 0
        ? matches.map((event) => ({
          key: event.value,
          label: event.title,
          description: event.when,
          icon: event.value === eventId ? Check : undefined,
          onClick: () => onChange(event),
        }))
        : [{ key: "none", label: events ? "No events match" : "Loading events…", disabled: true }]}
      trigger={trigger}
    />
  );
};

const DesignEditor = ({ onPickTemplate }) => {
  const {
    broadcast,
    template,
    setProcessedTemplate,
    autosaveTemplate,
    cancelAutosave,
    isAutosaving,
    selectedSendDomain,
    setTestModalOpen,
    emailMergeTags,
    sampleFanId,
  } = useBroadcastEditContext();
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const savedLabel = useSavedLabel(isAutosaving, broadcast.beefree_template.updated_at);
  const { usesEvent, eventId } = templateEvent(template);
  const templateName = broadcast.beefree_template.copied_from_blueprint?.name || broadcast.name;

  const changeEvent = (event) => {
    if (event.value === eventId) return;

    const retargeted = retargetEvent(template, event.value);
    cancelAutosave();
    setProcessedTemplate(retargeted);
    autosaveTemplate(retargeted);
    toastSuccess(`Event changed to ${event.title}`);
  };

  const sendTest = () => {
    if (!selectedSendDomain) {
      toastError("Pick a sender address in the Sender step to send a test.");
      return;
    }

    setTestModalOpen(true);
  };

  return (
    <>
      <div style={styles.toolbar}>
        <div style={styles.group}>
          <button type="button" style={styles.tile} aria-label="Change template" onClick={onPickTemplate}>
            <LayoutAlt01 width={20} height={20} aria-hidden="true" />
          </button>
          <div style={styles.templateDetails}>
            <button
              type="button"
              style={{ ...styles.metaButton, ...styles.title, maxWidth: "100%" }}
              aria-label={`Change template: ${templateName}`}
              onClick={onPickTemplate}
            >
              <span style={styles.truncate}>{templateName}</span>
              <ChevronDown width={14} height={14} color="var(--sl-text-quaternary)" aria-hidden="true" />
            </button>
            <div style={styles.meta}>
              <span>Template</span>
              <span aria-hidden="true">·</span>
              <VersionHistoryMenu savedLabel={savedLabel} onSeeAll={() => setShowVersionHistory(true)} />
              <span aria-hidden="true">·</span>
              <ErrorsContainer templatableContext={useBroadcastEditContext} compact />
            </div>
          </div>
        </div>
        {usesEvent && (
          <>
            <span style={styles.divider} aria-hidden="true" />
            <EventMenu eventId={eventId} onChange={changeEvent} />
          </>
        )}
        <span style={{ flex: 1 }} />
        <ButtonGroup
          items={[
            { label: "Preview", icon: Eye, onClick: () => setShowPreview(true) },
            { label: "Send test", icon: Send01, onClick: sendTest },
          ]}
        />
      </div>
      <TemplateEditor
        templatableContext={useBroadcastEditContext}
        autosave
        onAutosave={autosaveTemplate}
        mergeTags={emailMergeTags}
      />
      {showVersionHistory && <VersionHistoryPanel onClose={() => setShowVersionHistory(false)} />}
      <Drawer
        open={showPreview}
        onClose={() => setShowPreview(false)}
        side="right"
        width={previewDrawerWidth(template, !!sampleFanId)}
        backdrop
        title="Preview"
        subtitle="Switch between desktop and mobile, or pick a fan to fill in real event data."
      >
        {showPreview && <EmailPreviewTab />}
      </Drawer>
      <SendTestOverlay templatableContext={useBroadcastEditContext} />
    </>
  );
};

const ContentStep = ({ heading }) => {
  const { broadcast } = useBroadcastEditContext();
  const [picking, setPicking] = useState(false);
  const hasDesign = Boolean(broadcast.beefree_template?.template);

  if (!hasDesign || picking) {
    return (
      <div style={styles.galleryPage}>
        <TemplateGallery
          heading={heading}
          replacingDesign={hasDesign}
          onKeepDesign={() => setPicking(false)}
        />
      </div>
    );
  }

  return <DesignEditor onPickTemplate={() => setPicking(true)} />;
};

export default ContentStep;
